import _ from 'lodash';
import React, { createRef, FC, FunctionComponent, ReactElement, Suspense, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useValidationContext, validateRequiredTrimmed, ValidationStateProvider } from 'src/shared/validation';
import styled, { css } from 'styled-components';
import { BasicButton, DestructiveButton, PrimaryButton, TextButton } from '../buttons';
import { FormControl } from '../formControl';
import { ZTooltip } from '../ztooltip';
import { ReactType } from '../helpers/helpers';
import CloseIcon from 'src/shared/material_icons/navigation/close.svg';
import { Input } from '../input';
import { ZakekeLoading } from '../spinner';
import theme from '../../../shared/theme';
import { useDialogsState } from './dialogsState';
import { useEscapeToClose } from '../hooks/useEscapeToClose';
import { T } from 'src/shared/helpers';

const dialogsPortal = document.createElement('div');
dialogsPortal.id = 'dialogs-portal';
document.body.appendChild(dialogsPortal);

export function showDialogAction(id: string, dialog: React.ReactElement) {
    return {
        type: 'SHOW_DIALOG',
        id,
        dialog
    };
}

export function closeDialogAction(dialogId: string) {
    return {
        type: 'CLOSE_DIALOG',
        dialogId
    };
}

export const dialogContext = React.createContext({ dialogId: '', isForeground: false });

export const scrollbarsStyle = css`
    *::-webkit-scrollbar {
        width: 3px;
    }

    *::-webkit-scrollbar-track {
        background: ${(props) => props.theme.app.backgroundColor};
        border-radius: 20px;
    }

    *::-webkit-scrollbar-thumb {
        background: ${(props) => props.theme.app.primary_light};
        border-radius: 20px;
    }

    scrollbar-color: ${theme.app.primary_light} ${theme.app.backgroundColor};
    scrollbar-width: thin;
`;

export function useDialogManager() {
    const { dispatch } = useDialogsState();
    const { dialogId, isForeground } = useContext(dialogContext);
    // Stable identities: callers list these in hook dependencies, and a new function per render re-runs those hooks forever.
    const showDialog = useCallback((key: string, dialog: ReactElement) => dispatch(showDialogAction(key, dialog)), [dispatch]);
    const closeDialog = useCallback((key: string) => dispatch(closeDialogAction(key)), [dispatch]);

    return {
        currentDialogId: dialogId,
        currentDialogIsForeground: isForeground,
        showDialog,
        closeDialog
    };
}

const DialogOverlay = styled.div`
    position: fixed;
    background-color: rgba(0, 0, 0, 0.7);
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 40px;
    overflow: auto;
    z-index: 40;
    font-family: ${(props) => props.theme.app.font};

    ${scrollbarsStyle}
`;

export type DialogSize = 'small' | 'medium' | 'large' | 'xlarge' | 'fullscreen';

const dialogSizeMap: Record<DialogSize, string> = {
    small: '600px',
    medium: '800px',
    large: '1180px',
    xlarge: '1400px',
    fullscreen: '95%'
};

const DialogWindowContainer = styled.div<{ $size?: DialogSize }>`
    padding: 40px 40px 20px;
    background-color: white;
    border-radius: 5px;
    flex-basis: ${(props) => dialogSizeMap[props.$size || 'medium']};
    ${(props) => props.$size === 'fullscreen' && 'height: 100%;'}
    display: flex;
    flex-direction: column;
    position: relative;
`;

export const DialogWindowClose = styled(CloseIcon)`
    position: absolute;
    right: 16px;
    top: 16px;
    cursor: pointer;
    z-index: 999;
    width: 20px;
    height: 20px;

    &:hover {
        opacity: 0.5;
    }
`;

interface DialogWindowProps {
    className?: string;
    showCloseButton?: boolean;
    onClose: () => void;
    children?: React.ReactNode;
    isAddSalesChannel?: boolean;
    size?: DialogSize;
}

export const DialogWindow: FunctionComponent<DialogWindowProps> = ({ children, className, showCloseButton = true, onClose, size }) => {
    return (
        <DialogWindowContainer className={className} $size={size}>
            {showCloseButton && <DialogWindowClose onClick={onClose} />}
            {children}
        </DialogWindowContainer>
    );
};

// Side padding is for select outlines that must not be cropped
const DialogContent = styled.div<{ minHeight?: number }>`
    color: ${theme.app.component.card.text.color};
    overflow-y: auto;
    flex: 1;
    min-height: ${(p) => (p.minHeight ? p.minHeight : 0)}px;
`;

const DialogFooterButton = styled(PrimaryButton)`
    min-width: 150px;
`;

const DialogFooterOutlineButton = styled(BasicButton)`
    min-width: 150px;
`;

const DialogFooterDestructiveButton = styled(DestructiveButton)`
    min-width: 150px;
`;

const DialogFooterLinkButton = styled(TextButton)`
    min-width: 130px;
`;

const DialogFooter = styled.div<{
    alignButtons?: string;
    marginButtons?: number;
}>`
    display: flex;
    flex: 2;
    align-items: center;
    padding-top: 20px;
    ${(props) => props.alignButtons == 'left' && 'justify-content:flex-start'};
    ${(props) => props.alignButtons == 'center' && 'justify-content:center'};
    ${(props) => (props.alignButtons == 'right' || !props.alignButtons) && 'justify-content:flex-end'};
    ${DialogFooterButton}, ${DialogFooterOutlineButton}, ${DialogFooterDestructiveButton} {
        ${(props) => (!props.marginButtons && props.marginButtons !== 0 ? 'margin-left: 20px' : `margin-left: ${props.marginButtons}px`)};
    }
`;

const DialogFooterContentContainer = styled.div`
    display: flex;
`;

export const DialogTitle = styled.h1`
    color: ${theme.app.component.card.title.color};
    font-size: 14px;
    font-weight: 600;
    text-align: left;
    width: 100%;
    margin: 0 0 16px;
`;

const DialogTitleHtml = styled.div``;

export interface DialogButton {
    label: string;
    outline?: boolean;
    destructive?: boolean;
    linkButton?: boolean;
    disabled?: boolean;
    dataTrack?: string;
    onClick?: () => void;
    isSubmit?: boolean;
    tooltip?: string | React.ReactNode;
}

export interface DialogProps {
    closeOnOverlayClick?: boolean;
    showCloseButton?: boolean;
    children?: React.ReactNode;
    onClose?: () => void;
    title?: string;
    titleHtml?: React.ReactNode;
    size?: DialogSize;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    windowDecorator?: React.FunctionComponent<any>;
    footerContent?: React.ReactNode;
    /**
     * Esc closes the dialog (through `onClose`) when it is the one in front. `true` always, `false`
     * never; left out, it follows the page scope (`useEscapeClosesModals`) and only for dialogs
     * that show the close button — Esc does what the X does.
     */
    closeOnEscape?: boolean;
    buttons?: DialogButton[];
    alignButtons?: string;
    marginButtons?: number;
    minHeight?: number;
}

const DialogLoadingContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const DialogLoading = () => (
    <DialogLoadingContainer>
        <ZakekeLoading />
    </DialogLoadingContainer>
);

export const Dialog = React.forwardRef<HTMLDivElement, DialogProps>((props, ref) => {
    const Window = props.windowDecorator || DialogWindow;
    const { dispatch } = useDialogsState();
    const { dialogId } = useContext(dialogContext);
    const overlayRef = useRef<HTMLDivElement>(null);

    const onClose = props.onClose || (() => dispatch(closeDialogAction(dialogId)));
    useEscapeToClose(onClose, props.closeOnEscape ?? (props.showCloseButton === false ? false : undefined));
    const handleOverlayClick = (e: React.MouseEvent) => {
        if (props.closeOnOverlayClick && e.target === overlayRef.current) onClose();
    };

    return (
        <DialogOverlay ref={overlayRef} onClick={handleOverlayClick} data-dialog-overlay>
            {React.createElement(
                Window,
                { onClose, showCloseButton: props.showCloseButton, size: props.size },
                <React.Fragment>
                    {props.title && <DialogTitle>{props.title}</DialogTitle>}
                    {props.titleHtml && <DialogTitleHtml>{props.titleHtml}</DialogTitleHtml>}

                    <DialogContent minHeight={props.minHeight} ref={ref}>
                        <Suspense fallback={<DialogLoading />}>{props.children}</Suspense>
                    </DialogContent>

                    {(props.footerContent || props.buttons) && (
                        <DialogFooterContentContainer>
                            {props.footerContent}

                            {props.buttons && props.buttons.length > 0 && (
                                <DialogFooter alignButtons={props.alignButtons} marginButtons={props.marginButtons}>
                                    {props.buttons.map((button) => {
                                        let ButtonComponent = DialogFooterButton;
                                        if (button.outline) ButtonComponent = DialogFooterOutlineButton;
                                        if (button.destructive) ButtonComponent = DialogFooterDestructiveButton;
                                        const LinkButtonComponent = button.linkButton ? DialogFooterLinkButton : null;

                                        const buttonElement = LinkButtonComponent ? (
                                            <LinkButtonComponent data-track={button.dataTrack} onClick={() => button.onClick?.()}>
                                                {button.label}
                                            </LinkButtonComponent>
                                        ) : (
                                            <ButtonComponent
                                                type={button.isSubmit ? 'submit' : 'button'}
                                                disabled={button.disabled}
                                                data-track={button.dataTrack}
                                                onClick={() => button.onClick?.()}
                                            >
                                                {button.label}
                                            </ButtonComponent>
                                        );

                                        return (
                                            <React.Fragment key={button.label}>
                                                {button.tooltip ? (
                                                    <ZTooltip
                                                        title={typeof button.tooltip === 'string' ? button.tooltip : undefined}
                                                        html={typeof button.tooltip === 'string' ? undefined : button.tooltip}
                                                        position='top'
                                                        addContainer
                                                    >
                                                        {buttonElement}
                                                    </ZTooltip>
                                                ) : (
                                                    buttonElement
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </DialogFooter>
                            )}
                        </DialogFooterContentContainer>
                    )}
                </React.Fragment>
            )}
        </DialogOverlay>
    );
});

export const DialogsRenderer: FC = () => {
    const { state } = useDialogsState();
    // const hasDialogs = state.dialogs.length > 0;
    //
    // useEffect(() => {
    //     if (!hasDialogs) return;
    //     const body = document.body;
    //     const html = document.documentElement;
    //     const prevBodyOverflow = body.style.overflow;
    //     const prevHtmlOverflow = html.style.overflow;
    //     body.style.overflow = 'hidden';
    //     html.style.overflow = 'hidden';
    //     return () => {
    //         body.style.overflow = prevBodyOverflow;
    //         html.style.overflow = prevHtmlOverflow;
    //     };
    // }, [hasDialogs]);

    return (
        <>
            {createPortal(
                state.dialogs.map((x, idx) => (
                    <dialogContext.Provider key={x.id} value={{ dialogId: x.id, isForeground: idx === state.dialogs.length - 1 }}>
                        <Suspense
                            fallback={
                                <DialogOverlay data-dialog-overlay>
                                    {React.Children.map(x.dialog, (child) => {
                                        if (!React.isValidElement(child)) return;
                                        const props = child.props as DialogProps;

                                        return React.createElement(
                                            props.windowDecorator || DialogWindow,
                                            { showCloseButton: false, size: props.size, onClose: () => {} },
                                            <DialogLoading />
                                        );
                                    })}
                                </DialogOverlay>
                            }
                        >
                            {x.dialog}
                        </Suspense>
                    </dialogContext.Provider>
                )),
                dialogsPortal
            )}
        </>
    );
};

export const BasicDialogWindow = styled(DialogWindow)`
    max-width: 600px;
`;

// IL MESSAGGIO NON È UN PARAGRAFO. `message` è un ReactNode: chi chiama ci mette un elenco, due
// righe separate, un blocco — e dentro un <p> il browser chiude il paragrafo al primo elemento
// di blocco, sputando fuori il resto. L'albero che ne esce non è quello che React crede di
// avere, e la console lo dice («validateDOMNesting»). Un <div> accetta qualunque contenuto, e
// porta i margini che il browser dà a un paragrafo: nessuna delle dialog già scritte si muove.
const DialogMessage = styled.div`
    margin: 1em 0;
`;

export const MessageDialog: FunctionComponent<{ message: React.ReactNode } & DialogProps> = ({ message, ...props }) => {
    const { closeDialog } = useDialogManager();
    const { dialogId } = useContext(dialogContext);

    return (
        <Dialog
            size='small'
            buttons={[
                {
                    label: 'OK',
                    onClick: () => {
                        closeDialog(dialogId);
                        props.onClose?.();
                    }
                }
            ]}
            {...props}
        >
            <DialogMessage>{message}</DialogMessage>
        </Dialog>
    );
};

export const QuestionDialog: FunctionComponent<
    {
        message: React.ReactNode;
        buttonYesLabel?: string;
        buttonNoLabel?: string;
        disabledYes?: boolean;
        disabledNo?: boolean;
        destructiveNo?: boolean;
        // Disruptive confirmation (e.g. deletes): the confirm (Yes) button turns red and the cancel (No)
        // button becomes an outline button, so the destructive action is clearly signalled.
        destructive?: boolean;
        onYesClick?: () => void;
        onNoClick?: () => void;
    } & DialogProps
> = ({ message, buttonYesLabel, buttonNoLabel, disabledYes, disabledNo, destructiveNo, destructive, onYesClick, onNoClick, ...props }) => {
    const { closeDialog } = useDialogManager();
    const { dialogId } = useContext(dialogContext);

    return (
        <Dialog
            size='small'
            buttons={[
                {
                    label: buttonYesLabel || 'Yes',
                    outline: !destructive,
                    destructive: destructive ?? false,
                    disabled: disabledYes ?? false,
                    onClick: onYesClick || (() => closeDialog(dialogId))
                },
                {
                    label: buttonNoLabel || 'No',
                    outline: destructive ?? false,
                    destructive: !destructive && (destructiveNo ?? false),
                    disabled: disabledNo ?? false,
                    onClick: onNoClick || (() => closeDialog(dialogId))
                }
            ]}
            {...props}
        >
            <DialogMessage>{message}</DialogMessage>
        </Dialog>
    );
};

type InputControlProps = ReactType<HTMLInputElement>;
type InputDialogProps = {
    label: string;
    onConfirm: (value: string) => void;
    buttonLabel?: string;
} & DialogProps &
    InputControlProps;

const InputDialogContent: FunctionComponent<InputDialogProps> = ({ label, defaultValue, buttonLabel, onConfirm, required, ...props }) => {
    const [value, setValue] = useState((defaultValue as string) || '');
    const validationContext = useValidationContext();
    const inputProps = props as Omit<InputControlProps, 'ref'>;
    const inputRef = createRef<HTMLInputElement>();
    const validationKey = `inputDialog-${label}`;

    const handleConfirm = () => {
        if (required && !validationContext.submit()) return;
        onConfirm(value);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') handleConfirm();
    };

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    return (
        <Dialog
            size='small'
            buttons={[
                {
                    label: buttonLabel || 'Confirm',
                    onClick: handleConfirm
                }
            ]}
            {..._.omit(props, 'ref')}
        >
            <FormControl label={label}>
                <Input
                    ref={inputRef}
                    onKeyDown={handleKeyDown}
                    value={value}
                    onChange={(e) => setValue(e.currentTarget.value)}
                    validationKey={required ? validationKey : undefined}
                    validationLabel={label}
                    validate={required ? validateRequiredTrimmed : undefined}
                    validationMessage={T._(`${label} is required.`, 'Admin')}
                    required={required}
                    {...inputProps}
                />
            </FormControl>
        </Dialog>
    );
};

export const InputDialog: FunctionComponent<InputDialogProps> = (props) => (
    <ValidationStateProvider>
        <InputDialogContent {...props} />
    </ValidationStateProvider>
);
